import React, { useEffect, useMemo, useState } from "react";

/**
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - fileName?: string  // "comprobante.pdf" | "comprobante.xml"
 * - fileType?: string  // "application/pdf" | "application/xml" | "text/xml"
 * - origen: 's3' | 'base64' | 'system'   // 'system' se toma como 'base64'
 * - s3link?: string    // URL prefirmada (si viene como URL)
 * - fileData?: string  // base64 bruto (sin encabezado data:) si tu backend lo regresa así
 */
const FileViewerModal = ({
  isOpen,
  onClose,
  fileName,
  fileType,
  origen,
  s3link,
  fileData,
}) => {
  const [xmlText, setXmlText] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [pdfObjectUrl, setPdfObjectUrl] = useState("");

  // Normaliza 'system' => 'base64'
  const source = useMemo(() => (origen === "system" ? "base64" : origen), [origen]);

  const isPdf =
    (fileType && /pdf/i.test(fileType)) ||
    (fileName && fileName.toLowerCase().endsWith(".pdf"));
  const isXml =
    (fileType && /xml/i.test(fileType)) ||
    (fileName && fileName.toLowerCase().endsWith(".xml"));

  // Utils
  const base64ToUint8Array = (b64) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  };
  const base64ToUtf8 = (b64) => {
    try {
      return new TextDecoder().decode(base64ToUint8Array(b64));
    } catch {
      return "";
    }
  };

  // --------- PDF: construir Object URL (URL prefirmada o base64) ----------
  useEffect(() => {
    let revokeUrl = null;

    const makePdfFromPresignedUrl = async () => {
      if (!isOpen || !isPdf) return;
      if (source !== "s3" || !s3link || !/^https?:\/\//i.test(s3link)) return;
      setLoading(true);
      setErrorMsg("");
      try {
        const res = await fetch(s3link, { method: "GET" });
        if (!res.ok) throw new Error(`No se pudo cargar el PDF (HTTP ${res.status})`);
        let blob = await res.blob();
        // Fuerza mime si el servidor no lo pone correcto
        if (blob.type !== "application/pdf") {
          blob = new Blob([await blob.arrayBuffer()], { type: "application/pdf" });
        }
        const url = URL.createObjectURL(blob);
        setPdfObjectUrl(url);
        revokeUrl = url;
      } catch (e) {
        setErrorMsg(e && e.message ? e.message : "No se pudo cargar el PDF desde S3.");
        setPdfObjectUrl("");
      } finally {
        setLoading(false);
      }
    };

    const makePdfFromBase64 = () => {
      if (!isOpen || !isPdf) return;
      // Acepta base64 tanto si viene con source "base64" como si te lo pasan con "s3"
      if (!fileData) return;
      try {
        const bytes = base64ToUint8Array(fileData);
        const blob = new Blob([bytes], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        setPdfObjectUrl(url);
        revokeUrl = url;
      } catch (e) {
        setErrorMsg("No se pudo procesar el PDF base64.");
        setPdfObjectUrl("");
      }
    };

    // Reset anterior
    setPdfObjectUrl("");
    setErrorMsg("");

    // Caso 1: PDF proveniente de URL prefirmada
    if (isPdf && source === "s3" && s3link && /^https?:\/\//i.test(s3link)) {
      makePdfFromPresignedUrl();
    }
    // Caso 2: PDF en base64 (source "base64" o si tu backend lo da así aun con origen "s3")
    else if (isPdf && fileData) {
      makePdfFromBase64();
    }

    return () => {
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [isOpen, source, isPdf, s3link, fileData]);

  // --------- XML: cargar y pretty print ----------
  // XML desde base64
  useEffect(() => {
    if (!isOpen || !isXml) return;
    if (source !== "base64") return;
    setLoading(true);
    setErrorMsg("");
    try {
      setXmlText(fileData ? base64ToUtf8(fileData) : "");
    } finally {
      setLoading(false);
    }
  }, [isOpen, source, isXml, fileData]);

  // XML desde S3 (URL prefirmada)
  useEffect(() => {
    const controller = new AbortController();
    const loadXmlFromS3 = async () => {
      if (!isOpen || !isXml) return;
      if (source !== "s3" || !s3link || !/^https?:\/\//i.test(s3link)) return;
      setLoading(true);
      setErrorMsg("");
      try {
        const res = await fetch(s3link, { method: "GET", signal: controller.signal });
        if (!res.ok) throw new Error(`No se pudo cargar el XML (HTTP ${res.status})`);
        const txt = await res.text();
        setXmlText(txt);
      } catch (e) {
        if (e.name !== "AbortError") {
          setErrorMsg(e && e.message ? e.message : "No se pudo cargar el XML desde S3.");
        }
      } finally {
        setLoading(false);
      }
    };
    loadXmlFromS3();
    return () => controller.abort();
  }, [isOpen, source, isXml, s3link]);

  const prettyXml = useMemo(() => {
    const raw = xmlText || "";
    if (!raw) return "";
    try {
      const doc = new DOMParser().parseFromString(raw, "application/xml");
      if (doc.querySelector("parsererror")) return raw;
      let str = new XMLSerializer().serializeToString(doc);
      str = str
        .replace(/(>)(<)(\/*)/g, "$1\n$2$3")
        .split("\n")
        .map((l) => l.trim())
        .reduce(
          (acc, line) => {
            let change = 0;
            if (/^<\/.+>/.test(line)) change = -1;
            const indent = Math.max(acc.level + change, 0);
            acc.out.push("  ".repeat(indent) + line);
            if (/^<[^!?].*[^\/]>.*$/.test(line) && !/<\/.+>$/.test(line)) acc.level++;
            if (change === -1) acc.level = Math.max(acc.level, 0);
            return acc;
          },
          { out: [], level: 0 }
        ).out.join("\n");
      return str;
    } catch {
      return raw;
    }
  }, [xmlText]);

  const base64DataUrl = useMemo(() => {
    if (source !== "base64" || !fileData) return "";
    const mime = isPdf ? "application/pdf" : (fileType || "application/xml");
    return `data:${mime};base64,${fileData}`;
  }, [source, fileData, isPdf, fileType]);

  if (!isOpen) return null;

  const nombre =
    fileName || (isPdf ? "archivo.pdf" : isXml ? "archivo.xml" : "archivo.bin");

  const renderBody = () => {
    // PDF (Object URL ya preparado arriba)
    if (isPdf) {
      return (
        <>
          {loading && !pdfObjectUrl && <div>Cargando PDF…</div>}
          {errorMsg && !pdfObjectUrl && (
            <div className="alert alert-warning">{errorMsg}</div>
          )}
          {pdfObjectUrl && (
            <iframe
              title="PDF"
              src={pdfObjectUrl}
              style={{ width: "100%", height: 600, border: "none" }}
            />
          )}
          {/* Fallback si por alguna razón no se pudo blobificar pero tienes base64 */}
          {!pdfObjectUrl && base64DataUrl && (
            <iframe
              title="PDF-b64"
              src={base64DataUrl}
              style={{ width: "100%", height: 600, border: "none" }}
            />
          )}
        </>
      );
    }

    // XML
    if (isXml) {
      return (
        <>
          {loading && <div>Cargando XML…</div>}
          {!loading && errorMsg && (
            <div className="alert alert-warning">{errorMsg}</div>
          )}
          {!loading && !errorMsg && prettyXml && (
            <pre
              style={{
                width: "100%",
                height: 600,
                overflow: "auto",
                background: "#f8f9fa",
                borderRadius: 8,
                padding: 12,
                border: "1px solid #e9ecef",
                margin: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 13,
              }}
            >
              {prettyXml}
            </pre>
          )}
        </>
      );
    }

    // Otros tipos: intenta iframe con URL u object URL si existiera
    if (source === "s3" && s3link && /^https?:\/\//i.test(s3link)) {
      return (
        <iframe
          title="Archivo-S3"
          src={s3link}
          style={{ width: "100%", height: 600, border: "none" }}
        />
      );
    }
    if (source === "base64" && base64DataUrl) {
      return (
        <iframe
          title="Archivo-Base64"
          src={base64DataUrl}
          style={{ width: "100%", height: 600, border: "none" }}
        />
      );
    }

    return <p>Datos de archivo no válidos o incompletos.</p>;
  };

  const renderFooter = () => {
    return (
      <>
        {/* Descarga desde Object URL si existe (funciona bien con atributo download) */}
        {pdfObjectUrl && (
          <a href={pdfObjectUrl} download={nombre} className="btn btn-primary">
            Descargar
          </a>
        )}
        {/* Si no hay object URL, intenta descargar desde la fuente disponible */}
        {!pdfObjectUrl && source === "s3" && s3link && (
          <a
            href={s3link}
            className="btn btn-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Descargar
          </a>
        )}
        {!pdfObjectUrl && source === "base64" && base64DataUrl && (
          <a href={base64DataUrl} download={nombre} className="btn btn-primary">
            Descargar
          </a>
        )}

        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cerrar
        </button>
      </>
    );
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      role="dialog"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{fileName || "Archivo"}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">{renderBody()}</div>

          <div className="modal-footer">{renderFooter()}</div>
        </div>
      </div>
    </div>
  );
};

export default FileViewerModal;